/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { classNames } from '../utils/UNSAFE_classNames';
import { getColorEnum } from './RatingUtils';
import { ratingGaugeVars } from './themes/RatingGaugeContract.css';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useUser } from '../hooks/UNSAFE_useUser';
import { RatingGaugeVariantOptions, RatingGaugeStyles } from './themes/RatingGaugeStyles.css';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { RatingGaugeRedwoodTheme } from './themes/redwood/RatingGaugeTheme';
import { DvtThresholdsColors } from '../utils/UNSAFE_meterUtils';

type Props = {
  isSelected: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  color: 'neutral' | 'gold' | ColorProps['color'] | DvtThresholdsColors;
};

const RatingStar = ({ isSelected, isDisabled, isReadOnly, color = 'neutral' }: Props) => {
  const { forcedColors } = useUser();
  const isHighContrast = forcedColors === 'active';
  const needsHighContrastStyle = isHighContrast && isDisabled;
  const enumColor = getColorEnum(color);
  const customColors = assignInlineVars({
    [ratingGaugeVars.colorSelected]: color,
    [ratingGaugeVars.borderColorSelected]: color,
    [ratingGaugeVars.colorSelectedReadonly]: color
  });
  const {
    variantClasses,
    styles: {
      ratingStarBaseStyle,
      innerRatingStarColor,
      outerRatingStarColor,
      outerDisabledHighContrastBaseStyle,
      outerDisabledHighContrastSelectedStyle,
      outerDisabledHighContrastUnselectedStyle
    }
  } = useComponentTheme<RatingGaugeVariantOptions, RatingGaugeStyles>(RatingGaugeRedwoodTheme, {
    color: enumColor,
    selectionState: isSelected ? 'isSelected' : 'isNotSelected',
    disabledState: isDisabled ? 'isDisabled' : 'isNotDisabled',
    readonlyState: isReadOnly ? 'isReadonly' : 'isNotReadonly',
    highContrastState: isHighContrast ? 'isHighContrast' : 'isNotHighContrast'
  });

  return (
    <svg
      viewBox="0 0 36 36"
      height="100%"
      width="100%"
      style={!enumColor ? customColors : undefined}
      class={classNames([ratingStarBaseStyle, enumColor ? variantClasses : ''])}>
      <g>
        <path
          class={innerRatingStarColor}
          d="m18 1 5.0061 11.9524 12.9939 1.0344-9.9 8.4215 3.0246 12.5917-11.1246-6.7476-11.12461 6.7476 3.02461-12.5917-9.9-8.4215 12.9939-1.0344z"
        />
        <path
          class={classNames([
            outerRatingStarColor,
            needsHighContrastStyle ? outerDisabledHighContrastBaseStyle : '',
            needsHighContrastStyle
              ? isSelected
                ? outerDisabledHighContrastSelectedStyle
                : outerDisabledHighContrastUnselectedStyle
              : ''
          ])}
          d="m23.0061 12.9524-5.0061-11.9524-5.0061 11.9524-12.9939 1.0344 9.9 8.4215-3.02461 12.5917 11.12461-6.7476 11.1246 6.7476-3.0246-12.5917 9.9-8.4215zm10.5043 1.8394-8.5262 7.2528 2.6077 10.8562-9.5919-5.818-9.59192 5.818 2.60772-10.8562-8.52615-7.2528 11.19115-.891 4.3192-10.31227 4.3192 10.31227z"
        />
      </g>
    </svg>
  );
};

export { RatingStar };
